/**
 * Getting a chain of title out of the app.
 *
 * Everything else Shadowland produces is a deliverable — a runsheet, an
 * ownership report, an invoice. The chart was the one thing that could
 * only be looked at, and a chain of title is precisely the thing a client
 * is handed. The only way out was a screenshot of whatever happened to
 * fit on the screen.
 *
 * The drawing is exported by CLONING THE SVG THE APP IS ALREADY DRAWING
 * rather than by re-rendering the document into a second painter. A
 * second painter is a second set of shape paths, a second text layout, a
 * second arrowhead — and every one of them is a place the export can
 * quietly stop matching the screen. What you see is what comes out,
 * because it is the same drawing with the controls taken off it.
 *
 * What comes off is everything that is a control rather than part of the
 * chain: selection frames, resize handles, connection ports, waypoint
 * grips, alignment guides, the marquee, the half-drawn connector, the
 * tags that open documents, and the rings the chain check leaves behind.
 * A deliverable does not show which box the person who made it happened
 * to have selected, and it does not carry their working notes.
 */
import { zlibSync } from 'fflate';
import { pdfFromImagePages } from '../imagePdf.js';

/** Drawn on the canvas, but not part of the drawing. */
const CONTROLS = [
  '.dgm-frame', '.dgm-ports', '.dgm-waypoints', '.dgm-guide', '.dgm-spacing',
  '.dgm-marquee', '.dgm-connect-preview', '.dgm-doc-tag',
  // The rings the chain check draws. They are the examiner's working
  // notes, not part of the chain, and a client who receives a chart with
  // three boxes ringed in amber will ask what is wrong with their title.
  '.dgm-flag',
];

/**
 * The rules a standalone copy of the drawing cannot do without.
 *
 * Almost everything on the canvas carries its colours as attributes,
 * because they belong to the shape rather than to the theme. The card
 * behind a connector's label is the exception — it is the page showing
 * through, so it is painted by the stylesheet, and a clone that leaves
 * the stylesheet behind draws it as black.
 */
function styleFor(background) {
  return `.dgm-edge-label-card{fill:${background};}`;
}

/** Room around the drawing so nothing is cut off at the paper's edge. */
export const PADDING = 28;

/**
 * The box the whole chain occupies, in world units.
 *
 * Shapes AND every point of every route: a connector dragged out to a
 * waypoint, or a label pushed to the end of a long line, sits outside the
 * boxes it joins, and a bounding box taken from the shapes alone crops it.
 */
export function chartBounds(shapes, routes, padding = PADDING) {
  const xs = [];
  const ys = [];
  for (const shape of shapes || []) {
    xs.push(shape.x, shape.x + shape.w);
    ys.push(shape.y, shape.y + shape.h);
  }
  for (const route of routes || []) {
    for (const point of route.points || []) { xs.push(point.x); ys.push(point.y); }
  }
  if (!xs.length) return { x: 0, y: 0, w: 1, h: 1 };
  const x = Math.min(...xs) - padding;
  const y = Math.min(...ys) - padding;
  return {
    x,
    y,
    w: Math.max(1, Math.max(...xs) + padding - x),
    h: Math.max(1, Math.max(...ys) + padding - y),
  };
}

/**
 * A standalone SVG of the drawing, at its own size.
 *
 * The viewport transform is dropped rather than kept: on screen that <g>
 * carries the pan and zoom somebody happens to be looking through, and an
 * export that honoured it would hand over whatever corner of the chain
 * they had scrolled to.
 */
export function svgFromSurface(surface, { bounds, background = '#ffffff' }) {
  const clone = surface.cloneNode(true);
  clone.querySelectorAll(CONTROLS.join(',')).forEach((node) => node.remove());

  const inner = clone.querySelector('g[transform]');
  if (inner) inner.removeAttribute('transform');

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(Math.round(bounds.w)));
  clone.setAttribute('height', String(Math.round(bounds.h)));
  clone.setAttribute('viewBox', `${bounds.x} ${bounds.y} ${bounds.w} ${bounds.h}`);

  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = styleFor(background);
  clone.insertBefore(style, clone.firstChild);

  const paper = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  paper.setAttribute('x', String(bounds.x));
  paper.setAttribute('y', String(bounds.y));
  paper.setAttribute('width', String(bounds.w));
  paper.setAttribute('height', String(bounds.h));
  paper.setAttribute('fill', background);
  // First in the drawing order, so it is the paper everything else is
  // painted on. Without it a PNG comes out on a transparent ground,
  // which in most viewers is black — and a chain of title in white on
  // black is not what anybody meant to send their client.
  clone.insertBefore(paper, style.nextSibling);

  return new XMLSerializer().serializeToString(clone);
}

/**
 * How much to multiply the drawing by, held under a pixel ceiling.
 *
 * A section chain is a tall drawing, and three times a tall drawing is a
 * canvas the browser will refuse to allocate — silently, by handing back
 * a blank one. So the ask is a resolution and the answer is whatever
 * resolution fits, which degrades a huge chart rather than losing it.
 */
export function scaleFor(bounds, wanted, ceiling) {
  const area = Math.max(1, bounds.w * bounds.h);
  const fits = Math.sqrt(ceiling / area);
  return Math.max(1, Math.min(wanted, fits));
}

/** The drawing, rasterised. Resolves to a canvas at `scale` times size. */
export function rasterise(svgText, bounds, scale) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bounds.w * scale));
    canvas.height = Math.max(1, Math.round(bounds.h * scale));
    const image = new Image();
    // A data URL rather than a blob URL: a blob URL makes the image a
    // different origin as far as the canvas is concerned in some
    // browsers, and a tainted canvas cannot be read back — which is the
    // whole point of drawing it.
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
    image.onload = () => {
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas);
    };
    image.onerror = () => reject(new Error('The chart could not be drawn for export.'));
    image.src = url;
  });
}

/** A PNG blob of a canvas. */
export function pngFrom(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be made.'))),
      'image/png',
    );
  });
}

/**
 * A one-page PDF of a canvas, LOSSLESS.
 *
 * The screen-capture PDFs embed JPEG, which is right for a photograph of
 * a county page. A chain of title is line art — hairlines, 8pt type — and
 * JPEG rings around exactly that. So the pixels go in deflated instead,
 * which PDF reads natively as /FlateDecode, and fflate is already here
 * for the spreadsheet exports.
 *
 * One page at the drawing's own proportions rather than cut into letter
 * sheets. A chain is a single tall picture and cutting it into pages puts
 * the break through the middle of a conveyance; printers scale a page to
 * fit, and a reader can see the whole thing at once.
 */
export function pdfFrom(canvas, scale = 1) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  // RGBA off the canvas, RGB into the PDF: the alpha channel is a
  // separate object in PDF and the paper underneath is already opaque.
  const rgb = new Uint8Array(width * height * 3);
  for (let i = 0, o = 0; i < data.length; i += 4, o += 3) {
    rgb[o] = data[i];
    rgb[o + 1] = data[i + 1];
    rgb[o + 2] = data[i + 2];
  }
  return pdfFromImagePages([{
    data: zlibSync(rgb, { level: 6 }),
    filter: 'FlateDecode',
    width,
    height,
    // The page is the drawing's world size in points, so a party box
    // drawn 160 across comes out 160 points across — the proportions the
    // chart was arranged in.
    pointWidth: Math.max(1, Math.round(width / scale)),
    pointHeight: Math.max(1, Math.round(height / scale)),
  }]);
}

/** A filename that says what the chart is and when it was taken. */
export function chartFilename(title, extension, at = new Date()) {
  const stem = String(title || 'chain-of-title')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'chain-of-title';
  return `${stem}-${at.toISOString().slice(0, 10)}.${extension}`;
}

/** Hand a blob to the browser under a name. */
export function save(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not immediately: Safari has not started the download when click()
  // returns, and revoking first gives a saved file of zero bytes.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
